/** @format — React hook for live voice chat sessions */

import { useCallback, useEffect, useRef, useState } from "react";
import { isVoiceSupported, startVoiceSession } from "../voiceSession";

/**
 * @typedef {'idle' | 'requesting-mic' | 'connecting' | 'waiting-ready' | 'listening' | 'thinking' | 'speaking' | 'stopping'} VoiceSessionStatus
 * @typedef {'chat' | 'note' | null} VoiceInputMode
 */

/**
 * @param {object} options
 * @param {string | null} options.threadId
 * @param {string | null} [options.chatId]
 * @param {(message: string, context?: { mode: VoiceInputMode; userTranscript: string }) => void} [options.onError]
 * @param {(saved?: boolean) => void} [options.onTurnComplete]
 * @param {(components: unknown[]) => void} [options.onComponents]
 * @param {(event: unknown) => void} [options.onPaymentEvent]
 * @param {(messages: unknown[]) => void} [options.onTurnSaved]
 */
export function useVoiceSession({
  threadId,
  chatId,
  onError,
  onTurnComplete,
  onComponents,
  onPaymentEvent,
  onTurnSaved,
}) {
  const sessionRef = useRef(null);
  const modeRef = useRef(null);
  const userTranscriptRef = useRef("");
  const transcriptRunsRef = useRef({
    user: { settled: "", pending: "" },
    assistant: { settled: "", pending: "" },
  });
  const [status, setStatus] = useState("idle");
  const [mode, setMode] = useState(null);
  const [userTranscript, setUserTranscript] = useState("");
  const [assistantTranscript, setAssistantTranscript] = useState("");
  const [sessionError, setSessionError] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);

  const isActive = status !== "idle";

  userTranscriptRef.current = userTranscript;

  const handleStatus = useCallback((next) => {
    setStatus(next);
  }, []);

  const resetTranscriptRuns = useCallback(() => {
    transcriptRunsRef.current = {
      user: { settled: "", pending: "" },
      assistant: { settled: "", pending: "" },
    };
    userTranscriptRef.current = "";
  }, []);

  const handleTranscript = useCallback((event) => {
    const text = typeof event.text === "string" ? event.text : "";
    const role = event.role === "assistant" ? "assistant" : "user";
    const run = transcriptRunsRef.current[role];

    if (event.final) {
      // A final frame carries the whole aggregated sentence for the current
      // delta run only. One turn can hold several sentences, so the settled
      // text accumulates and just the in-progress run is replaced.
      const sentence = text.trim();
      if (sentence) run.settled = run.settled ? `${run.settled} ${sentence}` : sentence;
      run.pending = "";
    } else {
      run.pending += text;
    }

    const caption = [run.settled, run.pending].filter(Boolean).join(" ").trim();
    if (role === "user") {
      userTranscriptRef.current = caption;
      setUserTranscript(caption);
    } else {
      setAssistantTranscript(caption);
    }
  }, []);

  const detachSession = useCallback(() => {
    sessionRef.current = null;
    setMediaStream(null);
  }, []);

  const endSession = useCallback(
    async ({ clearTranscripts = false, clearMode = true, clearError = true } = {}) => {
      const current = sessionRef.current;
      sessionRef.current = null;
      setMediaStream(null);
      if (current) await current.stop();
      if (clearMode) {
        modeRef.current = null;
        setMode(null);
      }
      setStatus("idle");
      if (clearError) setSessionError(null);
      if (clearTranscripts) {
        resetTranscriptRuns();
        setUserTranscript("");
        setAssistantTranscript("");
      }
    },
    [resetTranscriptRuns],
  );

  useEffect(() => {
    return () => {
      void sessionRef.current?.stop();
      sessionRef.current = null;
    };
  }, []);

  useEffect(() => {
    void sessionRef.current?.stop();
    sessionRef.current = null;
    setMediaStream(null);
    modeRef.current = null;
    setStatus("idle");
    setMode(null);
    setSessionError(null);
    resetTranscriptRuns();
    setUserTranscript("");
    setAssistantTranscript("");
  }, [threadId, resetTranscriptRuns]);

  const start = useCallback(
    async (nextMode, threadIdOverride = null) => {
      const sessionThreadId = threadIdOverride || threadId;
      if (!sessionThreadId || sessionRef.current) return false;
      if (!isVoiceSupported()) {
        onError?.("Voice is not supported in this browser.", {
          mode: nextMode,
          userTranscript: "",
        });
        return false;
      }

      modeRef.current = nextMode;
      setMode(nextMode);
      setSessionError(null);
      resetTranscriptRuns();
      setUserTranscript("");
      setAssistantTranscript("");

      try {
        const session = await startVoiceSession({
          threadId: sessionThreadId,
          chatId: threadIdOverride || chatId || sessionThreadId,
          onStatus: handleStatus,
          onTranscript: handleTranscript,
          onComponents,
          onPaymentEvent,
          onTurnSaved,
          onTurnComplete: (frame) => {
            const currentUserTranscript = [
              transcriptRunsRef.current.user.settled,
              transcriptRunsRef.current.user.pending,
            ]
              .filter(Boolean)
              .join(" ")
              .trim();
            onTurnComplete?.(frame.saved, {
              userTranscript: currentUserTranscript,
              assistantTranscript:
                [
                  transcriptRunsRef.current.assistant.settled,
                  transcriptRunsRef.current.assistant.pending,
                ]
                  .filter(Boolean)
                  .join(" ")
                  .trim(),
            });
            resetTranscriptRuns();
            setUserTranscript("");
            setAssistantTranscript("");
          },
          onError: (message) => {
            const snapshot = userTranscriptRef.current;
            setSessionError(message);
            detachSession();
            setStatus("idle");
            onError?.(message, { mode: modeRef.current, userTranscript: snapshot });
          },
        });
        sessionRef.current = session;
        setMediaStream(session.stream || null);
        return true;
      } catch (err) {
        modeRef.current = null;
        setMode(null);
        setStatus("idle");
        setMediaStream(null);
        const message = err instanceof Error ? err.message : "Could not start voice session.";
        setSessionError(message);
        onError?.(message, { mode: nextMode, userTranscript: "" });
        return false;
      }
    },
    [
      threadId,
      chatId,
      handleStatus,
      handleTranscript,
      onComponents,
      onPaymentEvent,
      onTurnSaved,
      onTurnComplete,
      onError,
      detachSession,
      resetTranscriptRuns,
    ],
  );

  const stop = useCallback(async () => {
    await endSession({ clearMode: true });
  }, [endSession]);

  const cancel = useCallback(async () => {
    await endSession({ clearTranscripts: true, clearMode: true, clearError: true });
  }, [endSession]);

  const resetTranscripts = useCallback(() => {
    resetTranscriptRuns();
    setUserTranscript("");
    setAssistantTranscript("");
  }, [resetTranscriptRuns]);

  const clearSessionError = useCallback(() => {
    setSessionError(null);
    modeRef.current = null;
    setMode(null);
  }, []);

  return {
    status,
    mode,
    userTranscript,
    assistantTranscript,
    sessionError,
    mediaStream,
    isActive,
    start,
    stop,
    cancel,
    resetTranscripts,
    clearSessionError,
  };
}
